import {
  getAllUsersCount,
  getAllUsersWithRoles,
  queryKeys,
} from '@vgrubs/components';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { GetAllUsersResponse } from 'components/src/types/api/user.types';
import { UserRole } from 'components/src/types/user.types';

const PRELOAD_ROOT_MARGIN = '1000px';

const fetchUsers = async ({
  pageParam = 1,
  query = '',
  roles = [],
  deleted = false,
}: {
  pageParam?: number;
  query?: string;
  roles?: UserRole[];
  deleted?: boolean;
}) => {
  const { data } = await getAllUsersWithRoles({
    limit: 20,
    page: pageParam,
    query: query || '',
    roles: roles ?? [],
    deleted: deleted ?? false,
  });

  const users: GetAllUsersResponse[] = data?.data ?? [];
  const nextPage = users.length === 20 ? pageParam + 1 : undefined;

  return {
    users,
    nextPage,
  };
};

export const useAdminUsersInfiniteScroll = ({
  initialQuery = '',
  roles = [],
  deleted = false,
}: {
  initialQuery?: string;
  roles?: UserRole[];
  deleted?: boolean;
}) => {
  const [inputValue, setInputValue] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery);
  const observerRef = useRef<HTMLDivElement | null>(null);

  const debouncedSearch = useMemo(() => debounce(setQuery, 500), []);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    refetch,
  } = useInfiniteQuery({
    queryKey: queryKeys.admin.users.list(query, roles, deleted),
    queryFn: ({ pageParam = 1 }) =>
      fetchUsers({ pageParam, query, roles, deleted }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const current = observerRef.current;
    if (!current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        threshold: 0,
        rootMargin: `0px 0px ${PRELOAD_ROOT_MARGIN} 0px`,
      }
    );

    observer.observe(current);

    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  const { data: countData } = useQuery({
    queryKey: queryKeys.admin.users.countAll(query),
    queryFn: async () => {
      const { data: countResponse } = await getAllUsersCount({
        query: query || null,
      });
      return countResponse?.data;
    },
    enabled: true,
  });

  const { nonDeletedCount, deletedCount } = countData ?? {
    nonDeletedCount: 0,
    deletedCount: 0,
  };

  const totalCount = nonDeletedCount + deletedCount;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    debouncedSearch(e.target.value);
  };

  const users: GetAllUsersResponse[] =
    data?.pages.flatMap((page) => page.users) ?? [];

  const SCROLL_KEY = 'admin-users-scroll';

  const saveScrollPosition = () => {
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
  };

  useLayoutEffect(() => {
    if (users.length === 0) return;
    const saved = sessionStorage.getItem(SCROLL_KEY);
    if (saved !== null) {
      const scrollY = parseInt(saved, 10);
      if (!Number.isNaN(scrollY)) {
        sessionStorage.removeItem(SCROLL_KEY);
        window.scrollTo({ top: scrollY });
      }
    }
  }, [users.length]);

  return {
    data,
    users,
    inputValue,
    handleInputChange,
    observerRef,
    isFetchingNextPage,
    status,
    saveScrollPosition,
    refetch,
    nonDeletedCount,
    deletedCount,
    totalCount,
  };
};
